/**
 * Schedule Analysis → Data Standard tab.
 * Live view of the P6 project codes for the job's estimate (P6-PROJECT-NAMING §5) and the
 * Monday verdict (DCMA-14 + Schedule Data Standard #15) for every project coded Status = CUR.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { authenticatedApiCall } from '../../../utils/apiClient';

const STATUS_STYLE = {
  CUR: { bg: '#dcfce7', fg: '#166534' },
  SMD: { bg: '#dbeafe', fg: '#1e40af' },
  SCN: { bg: '#fef3c7', fg: '#92400e' },
  BAS: { bg: '#ede9fe', fg: '#5b21b6' },
  REF: { bg: '#f1f5f9', fg: '#334155' },
  ARC: { bg: '#f1f5f9', fg: '#64748b' },
  TMP: { bg: '#fee2e2', fg: '#991b1b' },
};
const LEVEL_COLOR = { pass: '#15803d', fail: '#b91c1c', warn: '#b45309', info: '#64748b' };

const Pill = ({ text }) => {
  if (!text) return <span style={{ color: '#cbd5e1' }}>—</span>;
  const s = STATUS_STYLE[text] || { bg: '#f8fafc', fg: '#334155' };
  return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 999, background: s.bg, color: s.fg, fontSize: 11, fontWeight: 600 }}>{text}</span>;
};

const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: 12, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

export default function DataStandardTab({ jobNumber, instance, accounts, api, btn, btnPrimary }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [estimate, setEstimate] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(async (withVerdict) => {
    if (!jobNumber) return;
    setBusy(true); setError(null);
    try {
      const q = [estimate ? `estimate=${encodeURIComponent(estimate)}` : null, withVerdict ? null : 'verdict=0'].filter(Boolean).join('&');
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${api}/data-standard${q ? `?${q}` : ''}`);
      setData(res.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  }, [instance, accounts, jobNumber, api, estimate]);

  useEffect(() => { load(false); }, [load]);

  const projects = data?.projects || [];
  const health = data?.health;
  const byId = new Map((health?.projects || []).map((p) => [String(p.projId), p]));
  const sorted = [...projects].sort((a, b) => {
    const rank = (s) => ({ CUR: 0, SMD: 1, SCN: 2, BAS: 3, REF: 4, ARC: 5, TMP: 6 }[s] ?? 7);
    return rank(a.codes.status) - rank(b.codes.status) || String(a.codes.party).localeCompare(String(b.codes.party)) || String(b.projShortName).localeCompare(String(a.projShortName));
  });

  return (
    <section>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <button type="button" style={btn} onClick={() => load(false)} disabled={busy}>{busy ? 'Loading…' : 'Refresh codes'}</button>
        <button type="button" style={btnPrimary} onClick={() => load(true)} disabled={busy}>{busy ? 'Running…' : 'Run Monday check now'}</button>
        <input
          value={estimate}
          onChange={(e) => setEstimate(e.target.value)}
          placeholder={data?.estimateKey ? `estimate ${data.estimateKey}` : 'estimate (if the job is not mapped)'}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12, width: 220 }}
        />
        {data?.estimateKey && <span style={{ fontSize: 12, color: '#64748b' }}>P6 projects of estimate <b>{data.estimateKey}</b> · {projects.length} coded</span>}
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{error}</div>}
      {(data?.warnings || []).map((w) => <div key={w} style={{ background: '#fef3c7', color: '#92400e', padding: 8, borderRadius: 8, fontSize: 12, marginBottom: 8 }}>△ {w}</div>)}

      {health && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 10, marginBottom: 14 }}>
          {(health.projects || []).map((r) => {
            const col = r.verdict === 'accepted' ? '#15803d' : r.verdict === 'returned' ? '#b91c1c' : '#64748b';
            return (
              <div key={r.projId} style={{ border: '1px solid #e2e8f0', borderLeft: `5px solid ${col}`, borderRadius: 10, padding: 12, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{r.projShortName}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{r.projName}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{r.party} · DD {r.dataDate || '—'} · {r.activityCount ?? '—'} activities{r.asReceived ? ' · kept as received' : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 800, color: col, textTransform: 'uppercase', fontSize: 12 }}>{r.verdict}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>DCMA {r.dcmaScore ?? '—'}</div>
                  </div>
                </div>
                {r.error && <div style={{ fontSize: 12, color: '#991b1b', marginTop: 6 }}>{r.error}</div>}
                <div style={{ marginTop: 8 }}>
                  {(r.mandatoryFails || []).map((f) => <div key={f} style={{ fontSize: 12, color: '#b91c1c' }}>● {f}</div>)}
                  {(r.standardFails || []).map((f) => <div key={f} style={{ fontSize: 12, color: '#b45309' }}>○ {f}</div>)}
                  {(r.warnings || []).map((f) => <div key={f} style={{ fontSize: 12, color: '#64748b' }}>△ {f}</div>)}
                  {!r.error && !(r.mandatoryFails || []).length && !(r.standardFails || []).length && <div style={{ fontSize: 12, color: '#15803d' }}>All mandatory and standard checks pass</div>}
                </div>
                {r.standard?.findings && (
                  <button type="button" style={{ ...btn, marginTop: 8, padding: '4px 8px', fontSize: 11 }} onClick={() => setOpen(open === r.projId ? null : r.projId)}>
                    {open === r.projId ? 'Hide' : 'Show'} the {r.standard.findings.length} data-standard findings
                  </button>
                )}
                {open === r.projId && (r.standard?.findings || []).map((f) => (
                  <div key={f.check} style={{ fontSize: 11, marginTop: 4, color: LEVEL_COLOR[f.level] || '#334155' }}>
                    <b>{f.level}</b> {f.check}: {f.message}
                    {(f.items || []).slice(0, 8).map((it, i) => <div key={i} style={{ color: '#475569', paddingLeft: 10 }}>{Object.values(it).filter((v) => v != null).join(' · ')}</div>)}
                  </div>
                ))}
              </div>
            );
          })}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#fff', fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Global data</div>
            <div style={{ color: health.globals?.level === 'pass' ? '#15803d' : '#b91c1c' }}>{health.globals?.message}</div>
            <div style={{ color: '#64748b', marginTop: 6 }}>Checked {String(health.checkedAt || '').slice(0, 16).replace('T', ' ')} · {health.summary?.accepted} accepted · {health.summary?.returned} returned</div>
            <div style={{ color: '#64748b', marginTop: 6 }}>Same check runs Mondays 18:00 and is e-mailed to the <i>ims_weekly_health</i> list (Admin → Notifications).</div>
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={th}>Project ID</th><th style={th}>Name</th><th style={th}>Resp Party</th><th style={th}>Scope</th><th style={th}>Status</th><th style={th}>Main Cal.</th><th style={th}>Package</th><th style={th}>Rev</th><th style={th}>Data date</th><th style={th}>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => {
              const v = byId.get(String(p.projId));
              const missing = ['party', 'scope', 'status', 'calendar', 'package'].filter((k) => !p.codes[k]);
              return (
                <tr key={p.projId} style={{ background: p.codes.status === 'CUR' ? '#f0fdf4' : undefined }}>
                  <td style={{ ...td, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>{p.projShortName}</td>
                  <td style={{ ...td, maxWidth: 420 }}>{p.projName}{missing.length > 0 && <div style={{ color: '#b91c1c', fontSize: 11 }}>missing: {missing.join(', ')}</div>}</td>
                  <td style={td}><Pill text={p.codes.party} /></td>
                  <td style={td}><Pill text={p.codes.scope} /></td>
                  <td style={td}><Pill text={p.codes.status} /></td>
                  <td style={td}>{p.codes.calendar || '—'}</td>
                  <td style={td}>{p.codes.package || '—'}</td>
                  <td style={td}>{p.codes.rev || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.dataDate ? String(p.dataDate).slice(0, 10) : '—'}</td>
                  <td style={td}>{v ? <span style={{ fontWeight: 700, color: v.verdict === 'accepted' ? '#15803d' : v.verdict === 'returned' ? '#b91c1c' : '#64748b' }}>{v.verdict}</span> : (p.codes.status === 'CUR' ? <span style={{ color: '#94a3b8' }}>run check</span> : '')}</td>
                </tr>
              );
            })}
            {!busy && sorted.length === 0 && <tr><td style={td} colSpan={10}>No coded P6 projects for this estimate. Projects get their five codes on import (P6-RULES-FOR-SCHEDULERS).</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
        Rules: one <b>CUR</b> per Resp Party · the Monday check runs only on CUR files · files coded AFR or REF are kept as received (ID style, UDFs, calendars reported, not returned) · ID = <code>{data?.estimateKey || 'estimate'}-YYYYMMDD-N</code> for anything received or issued, <code>-Current-N</code> for LGL working files.
      </div>
    </section>
  );
}
